const UserFacingError = require('@/libraries/DigiTickets/Errors/UserFacingError');
const { PublicClientApplication } = require('@azure/msal-browser');

/**
 * Handles authentication by Microsoft Entra ID, formerly called Azure Active Directory, and sometimes incorrectly
 * called Active Directory.
 *
 * Two approaches were tried when implementing this.
 * 1) Using the npm package @azure/msal-browser, using that to login the user and get a token,
 *    then sending that token to the API to get the user details.
 * 2) Using the API to generate a Microsoft login URL, sending the user there, the user returns to ProPoint with a
 *    token in the hash then sending that token to the API to get the user details.
 *
 * There are pros and cons of each way. In the end #1 was used because it provides a more seamless experience for
 * the user, and avoid security issues redirecting back to ProPoint with the token in the URL.
 *
 * To use this service the `init` method must be called first.
 * That will fetch some data from the DT API about our application configuration on Entra ID.
 * Then it will create a PublicClientApplication instance from Microsoft's SDK.
 * Once that's dont you can call the `login` or `logout` method.
 *
 * `login` will show a popup to the user where they can login to their Microsoft account. Once done, the popup
 * window will close and the promise resolves. This service sends the idToken received in that promise to the DT API
 * which will process it and return a DigiTickets user.
 *
 * `logout` will show a popup window the user where they can choose to logout of their Microsoft account. If the user
 * does not do this they will remain logged in to Microsoft and another PP user can come along and just press the
 * Entra ID login button and login as them.
 */
class EntraIDAuthService {
    /**
     * @param {ApiClient} apiClient
     * @param {AppConfig} appConfig
     * @param {LocalStorageDataStore} localStorageDataStore
     */
    constructor(
        apiClient,
        appConfig,
        localStorageDataStore
    ) {
        this.apiClient = apiClient;
        this.appConfig = appConfig;
        this.localStorageDataStore = localStorageDataStore;

        this.entraIDAppName = 'propoint';

        /**
         * @type {PublicClientApplication}
         */
        this.msal = null;
        this.msalInitializing = false;
        this.msalInitialized = false;
    }

    /**
     * @return {boolean}
     */
    isInitialized() {
        return !!this.msalInitialized;
    }

    /**
     * This must be called before any of the other methods.
     *
     * @return {Promise}
     */
    async init() {
        if (this.msalInitializing) {
            throw new Error('Already initializing');
        }

        if (this.msalInitialized) {
            return true;
        }

        this.msalInitializing = true;

        // Fetch info about the Entra ID application from the DT API (or from local storage if cached).
        const entraAppInfo = await this.getAppInfoFromApi();
        if (!entraAppInfo) {
            throw new Error('Failed to get Entra app info from API.');
        }

        // Configuration for the Microsoft instance...
        // See https://learn.microsoft.com/en-us/azure/active-directory/develop/tutorial-v2-javascript-auth-code#configure-your-javascript-spa

        // We don't use the redirect flow, but a redirect URL is required anyway, so generate one.
        const redirectUrl = this.appConfig.getEposUrl(this.appConfig.getCurrentScreen());
        const entraAppConfig = {
            auth: {
                clientId: entraAppInfo.clientID,
                redirectUri: redirectUrl,
                postLogoutRedirectUri: redirectUrl,
                navigateToLoginRequestUrl: false,
            },
        };

        // Set the tenant if supplied in the app info.
        // This limits the organisation that the user is allowed to log in to and displays their branded
        // login page in the popup.
        // Currently only NT uses this because they have their own infrasturcture, so we know which organisation
        // they want. For the rest of DT we can't support this currently.
        if (entraAppInfo.defaultTenantID) {
            entraAppConfig.auth.authority = `https://login.microsoftonline.com/${entraAppInfo.defaultTenantID}`;
        }

        // See https://azuread.github.io/microsoft-authentication-library-for-js/ref/classes/_azure_msal_browser.PublicClientApplication.html#constructor
        // for documentation on this object and its methods.
        this.msal = new PublicClientApplication(entraAppConfig);

        await this.msal.initialize();
        this.msalInitializing = false;
        this.msalInitialized = true;

        return true;
    }

    /**
     * Start the Entra ID redirect login flow.
     * This will redirect the user to a page where they can login to their Microsoft account. The user will
     * then be sent back to ProPoint with a code in the hash.
     *
     * https://learn.microsoft.com/en-us/entra/identity-platform/scenario-spa-sign-in?tabs=javascript2#sign-in-with-a-redirect
     */
    async loginWithRedirect() {
        const loginRequest = {
            scopes: ['User.Read'],
        };

        // When you call loginRedirect it will store an "interaction in progress" setting in the browser's
        // session storage. If something goes wrong that leaves us unable to attempt the login again because
        // it says it is already in progress. Clear the storage before continuing.
        // This is disabled again for now because it's possible it removes genuine data stored in the cache.
        // await this.msal.controller.browserStorage.clear();

        return this.msal.loginRedirect(loginRequest);
    }

    /**
     * Start the Entra ID popup login flow.
     * This will show a popup where the user can login to their Microsoft account. When the window closes
     * the promise will resolve.
     *
     * https://learn.microsoft.com/en-us/entra/identity-platform/scenario-spa-sign-in?tabs=javascript2#sign-in-with-a-pop-up-window
     */
    async loginWithPopup() {
        const loginRequest = {
            scopes: ['User.Read'],
        };

        let entraLoginResult;
        try {
            entraLoginResult = await this.msal.loginPopup(loginRequest);
        } catch (e) {
            console.error(e);
            // Errors thrown from the login call have a message that looks like this:
            // "user_cancelled: User cancelled the flow."
            // Remove the first part of that message. We split shift and join because there could be
            // other ": "s in the message later.
            const messageParts = e.message.split(': ');
            messageParts.shift();
            throw new UserFacingError(messageParts.join(': '), null);
        }

        if (!entraLoginResult || !entraLoginResult.idToken) {
            throw new Error('Failed to get ID token from Entra ID. Result:', entraLoginResult);
        }

        return this.getUserDataFromIDToken(entraLoginResult.idToken);
    }

    /**
     * Fetch info about the Entra ID app from the API.
     * The info returned will vary depending on the DT environment, but will always be the same for each environment.
     * The benefit of getting it from the API is we don't need to maintain the Client IDs etc. on ProPoint as well
     * as in the backend code.
     * Note that the app is not related to the DT client. We have one Entra ID app per environment, and many clients
     * will use the same DT app (NT has their own app).
     *
     * @return {Promise<{appName: string, clientID: string}>}
     */
    async getAppInfoFromApi() {
        const cachedData = this.localStorageDataStore.find(EntraIDAuthService.CACHE_KEY);
        if (cachedData) {
            console.debug('Using cached Entra ID app info.', cachedData);
            return cachedData;
        }

        return this.apiClient
            .request(
                'GET',
                'users/auth/entra-id/app-info',
                {
                    appName: this.entraIDAppName,
                }
            )
            .then((response) => {
                const data = response?.data;
                console.debug('Fetched new Entra ID app info.', data);
                if (data) {
                    this.localStorageDataStore.persist(EntraIDAuthService.CACHE_KEY, data);
                }
                return data;
            });
    }

    /**
     * @return {Promise<boolean>}
     */
    async entraIDAppExistsForCurrentEnvironment() {
        try {
            const appInfo = this.getAppInfoFromApi();
            return !!appInfo;
        } catch {
            return false;
        }
    }

    /**
     * Send an idToken from Microsoft to the DT API to parse and load info about a DT user from it.
     *
     * The API has the ability to automatically create a DT user if a new Entra ID user logs in we haven't seen before,
     * although it is intentionally not enabled yet on the API side for security.
     * But we do not use that. Users must first login to the DT Back Office to be set up before they can use ProPoint.
     *
     * @param {string} idToken
     *
     * @return {Promise<{user: {}}>}
     */
    getUserDataFromIDToken(idToken) {
        return this.apiClient
            .request(
                'POST',
                'users/auth/entra-id',
                {
                    idToken,
                    appName: this.entraIDAppName,
                    resolve: '*',
                    encodePrintableLogos: 1,
                }
            )
            .then((response) => {
                // Add the ID token to the user data so it gets hydrated into the User model.
                if (response?.data?.user) {
                    response.data.user.entraIDToken = idToken;
                }

                return response.data;
            });
    }

    /**
     * Display a popup for the user to logout of the Microsoft account they are currently logged in with.
     * This is called when the logout or switch user button is pressed.
     *
     * @return {Promise}
     */
    async logout() {
        return this.msal.logoutPopup();
    }
}

// Key used to cached the Entra ID app info in local storage.
EntraIDAuthService.CACHE_KEY = 'entraIDAppInfo';

module.exports = EntraIDAuthService;
